import React, { useState, useRef, useEffect } from 'react';
import "./Kitchen.css";
function useInView() {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) setInView(true);
    }, {
      threshold: 0.1
    });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, inView];
}
const categories = ['Signatures', 'Small Plates', 'The Grill', 'Desserts'];
const menuItems = {
  'Signatures': [{
    name: 'Wild Elephant Biryani',
    desc: 'Slow-cooked in a sealed handi, with saffron, caramelised onion and house-smoked butter. Served with raita and shorba.',
    price: 'Rs 520',
    tag: 'Chef\'s Pick'
  }, {
    name: 'Burnt Butter Prawns',
    desc: 'Tiger prawns in a habanero-mango glaze, charred lime and micro herbs. Pairs brilliantly with Tusker Gold.',
    price: 'Rs 680',
    tag: 'Bestseller'
  }, {
    name: 'Jungle Bird Tacos',
    desc: 'Pulled chicken tikka, avocado raita, pickled jalapenos and pomegranate seeds in a coal-charred tortilla.',
    price: 'Rs 420'
  }, {
    name: 'Elephant Walk Pizza',
    desc: 'Stone-baked with tandoori sauce, pulled lamb, caramelised onion, feta and fresh coriander oil.',
    price: 'Rs 560',
    tag: 'New'
  }],
  'Small Plates': [{
    name: 'Truffle Seekh Kebab',
    desc: 'Minced lamb with black truffle oil, garam masala and a saffron aioli dip.',
    price: 'Rs 380',
    tag: 'Premium'
  }, {
    name: 'Corn & Cheese Tikkis',
    desc: 'Crispy sweet corn cakes stuffed with smoked cheddar and jalapenos. Served with sriracha mayo.',
    price: 'Rs 280',
    tag: 'Veg'
  }, {
    name: 'Calamari al Carbon',
    desc: 'Grilled baby squid, chilli-garlic butter, crispy capers and charred lemon.',
    price: 'Rs 320'
  }, {
    name: 'Beer-Battered Onion Rings',
    desc: 'Brewed in our house IPA batter, served with smoked chipotle dip and parmesan dust.',
    price: 'Rs 220'
  }],
  'The Grill': [{
    name: '250g Wagyu Burger',
    desc: 'Wagyu patty, aged cheddar, beer-braised onion jam, pickles and wild truffle mayo on a brioche bun.',
    price: 'Rs 780',
    tag: 'Signature'
  }, {
    name: 'Whole Grilled Basa',
    desc: 'Herb-marinated basa with Konkan coconut chutney, pickled mango and steamed rice.',
    price: 'Rs 620'
  }, {
    name: 'Lamb Chops & Chimichurri',
    desc: 'French-trimmed lamb chops, smoky chimichurri, grilled corn polenta and roasted root vegetables.',
    price: 'Rs 860',
    tag: 'Chef\'s Pick'
  }, {
    name: 'Jackfruit Sheekh',
    desc: 'Raw jackfruit, charcoal-grilled with spiced cashew cream, pickled red onion and mint chutney.',
    price: 'Rs 360',
    tag: 'Veg'
  }],
  'Desserts': [{
    name: 'Stout Chocolate Lava',
    desc: 'Warm dark chocolate fondant with Midnight Stampede stout reduction and vanilla bean gelato.',
    price: 'Rs 280',
    tag: 'Beer Pairing'
  }, {
    name: 'Saffron Panna Cotta',
    desc: 'Silky saffron and cardamom panna cotta with rose gel, pistachio brittle and gold dust.',
    price: 'Rs 240'
  }, {
    name: 'Mango Sticky Rice',
    desc: 'Thai-style with Alphonso mango, coconut cream and black sesame toffee.',
    price: 'Rs 220',
    tag: 'Seasonal'
  }, {
    name: 'Cheese Board',
    desc: 'Selection of artisan cheeses, house-made fruit preserves, honeycomb and sourdough crackers.',
    price: 'Rs 480',
    tag: 'Share'
  }]
};
export default function Kitchen() {
  const [ref, inView] = useInView();
  const [activeCategory, setActiveCategory] = useState('Signatures');
  const [hovered, setHovered] = useState(null);
  return <section id="kitchen" ref={ref} className="kitchen__auto-1">
      {/* Accent */}
      <div className="kitchen__auto-2" />

      <div className="kitchen__auto-3">
        {/* Header */}
        <div style={{
        textAlign: 'center',
        marginBottom: 'clamp(48px, 6vw, 80px)',
        opacity: inView ? 1 : 0,
        transform: inView ? 'translateY(0)' : 'translateY(30px)',
        transition: 'all 0.8s ease'
      }}>
          <div className="kitchen__auto-4">
            <div className="kitchen__auto-5" />
            <span className="kitchen__auto-6">The Kitchen</span>
            <div className="kitchen__auto-7" />
          </div>
          <h2 className="kitchen__auto-8">
            Wild Kitchen
          </h2>
          <p className="kitchen__auto-9">
            Bold Indian soul, global technique - every dish crafted to complement the brew in your hand
          </p>
        </div>

        {/* Category tabs */}
        <div style={{
        display: 'flex',
        justifyContent: 'center',
        gap: '0',
        marginBottom: '56px',
        borderBottom: '1px solid rgba(230,210,173,0.1)',
        opacity: inView ? 1 : 0,
        transition: 'opacity 0.8s ease 0.2s',
        overflowX: 'auto'
      }}>
          {categories.map(cat => <button key={cat} onClick={() => setActiveCategory(cat)} style={{
          background: 'transparent',
          border: '1px solid transparent',
          padding: '12px 28px',
          cursor: 'pointer',
          fontFamily: "'Jost', sans-serif",
          fontSize: '18px',
          letterSpacing: '2.5px',
          textTransform: 'uppercase',
          color: activeCategory === cat ? '#9A321D' : 'rgba(255,246,232,0.62)',
          transition: 'all 0.3s ease',
          marginBottom: '-1px',
          whiteSpace: 'nowrap'
        }}>
              {cat}
            </button>)}
        </div>

        {/* Menu grid */}
        <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
        gap: '1px',
        background: 'rgba(230,210,173,0.08)',
        opacity: inView ? 1 : 0,
        transition: 'opacity 0.8s ease 0.3s'
      }}>
          {menuItems[activeCategory].map((item, i) => <div key={i} onMouseEnter={() => setHovered(i)} onMouseLeave={() => setHovered(null)} style={{
          background: hovered === i ? '#1A1A1A' : '#050505',
          padding: 'clamp(24px, 3vw, 36px)',
          transition: 'background 0.3s ease',
          cursor: 'default',
          position: 'relative'
        }}>
              {/* Tag */}
              {item.tag && <span className="kitchen__auto-10">
                  {item.tag}
                </span>}

              <div className="kitchen__auto-11">
                <h3 style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontSize: 'clamp(19px, 1.4vw, 22px)',
              fontWeight: 500,
              color: hovered === i ? '#F5E6C9' : '#FFF6E8',
              transition: 'color 0.3s ease',
              lineHeight: 1.3
            }}>
                  {item.name}
                </h3>
                <span className="kitchen__auto-12">
                  {item.price}
                </span>
              </div>

              <p className="kitchen__auto-13">
                {item.desc}
              </p>

              {/* Hover underline */}
              <div style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            height: '1px',
            width: hovered === i ? '100%' : '0',
            background: '#9A321D',
            transition: 'width 0.4s ease',
            opacity: 0.4
          }} />
            </div>)}
        </div>

        <div style={{
        textAlign: 'center',
        marginTop: '48px',
        opacity: inView ? 1 : 0,
        transition: 'opacity 0.8s ease 0.5s'
      }}>
          <span className="kitchen__auto-14">
            Full menu available at the bar - Ask for today's specials
          </span>
        </div>
      </div>
    </section>;
}
